import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import { FilterProvider, useFilters } from './context/FilterContext';
import { API_BASE } from './services/apiService';
import Header from './components/Header';
import FilterBar from './components/FilterBar';
import PreprocessPage from './pages/PreprocessPage';
import SinglePageDashboard from './pages/SinglePageDashboard';
import Chatbot from './components/Chatbot';
import GuidedTour from './components/GuidedTour';
import PolicyExtractorPage from './pages/PolicyExtractorPage';
import './App.css';

// Print header — shows client name on every PDF page
const PrintHeader = () => {
  const { clientInfo } = useFilters();
  return (
    <div className="print-header" aria-hidden="true">
      Claims Analysis Report
    </div>
  );
};

// Dashboard view — shown when data is loaded
const DashboardView = () => {
  useEffect(() => {
    // Push a dummy state so back button comes here first
    window.history.pushState({ dashboard: true }, '');
    const handleBack = (e) => {
      // When back is pressed on dashboard, go to home
      window.location.href = '/';
    };
    window.addEventListener('popstate', handleBack);
    return () => window.removeEventListener('popstate', handleBack);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Print-only: client name on first page */}
      <PrintHeader />
      <Header />
      <FilterBar />
      <SinglePageDashboard />
      <Chatbot />
      <GuidedTour />
      <div className="print-logo" aria-hidden="true">
        <img src="/pazcare-logo.svg" alt="" style={{ height: '18px' }} />
      </div>
    </div>
  );
};

// Load a saved dashboard by job ID — direct access (no auth)
const LoadDashboard = () => {
  const { jobId } = useParams();
  const { isDataLoaded, loadData } = useFilters();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isDataLoaded) { setLoading(false); return; }
    fetch(`${API_BASE}/api/job/result/${jobId}`)
      .then(r => r.json())
      .then(res => {
        if (res.success) {
          loadData(res.clientInfo, res.totalRows);
        } else {
          setError(res.error || 'Dashboard not found');
        }
        setLoading(false);
      })
      .catch(() => { setError('Failed to load dashboard'); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  if (isDataLoaded) return <DashboardView />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-violet-50 flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <img src="/pazcare-logo.svg" alt="PazCare" className="h-8 mx-auto mb-3" />
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
          {loading && (
            <div className="text-center py-4">
              <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-xs text-gray-500">Loading dashboard...</p>
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-4">
              <p className="text-sm text-red-600 mb-4">{error}</p>
              <a href="/" className="text-xs text-purple-600 hover:text-purple-800 underline">Go to upload page</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main flow — PreprocessPage (data + upload) → OTP → Dashboard
const MainFlow = () => {
  const { isDataLoaded } = useFilters();
  const [currentJobId, setCurrentJobId] = useState(null);

  // When data loads and we have a job ID, update URL
  useEffect(() => {
    if (isDataLoaded && currentJobId) {
      window.history.replaceState(null, '', `/dashboard/${currentJobId}`);
    }
  }, [isDataLoaded, currentJobId]);

  const handleDataLoaded = (jobId) => {
    // Update URL BEFORE dashboard renders so API calls get the correct job_id
    window.history.replaceState(null, '', `/dashboard/${jobId}`);
    setCurrentJobId(jobId);
  };

  // Dashboard loaded
  if (isDataLoaded) return <DashboardView />;

  // Main page — PreprocessPage handles everything (data input + upload + OTP + generate)
  return <PreprocessPage onJobCreated={handleDataLoaded} />;
};

function App() {
  return (
    <BrowserRouter>
      <FilterProvider>
        <Routes>
          <Route path="/dashboard/:jobId" element={<LoadDashboard />} />
          <Route path="/policy-extractor" element={<PolicyExtractorPage />} />
          <Route path="*" element={<MainFlow />} />
        </Routes>
      </FilterProvider>
    </BrowserRouter>
  );
}

export default App;
